import MojitoCore from 'mojito/core';
import MojitoServices from 'mojito/services';
import MojitoPresentation from 'mojito/presentation';
import MojitoModules from 'mojito/modules';
import { connect } from 'react-redux';

const log = MojitoCore.logger.get('SportCouponPageView');
const UIViewImplementation = MojitoCore.Presentation.UIViewImplementation;
const EventGroupsDataDescriptor = MojitoServices.EventGroups.descriptor;
const SportsMenu = MojitoModules.SportsMenu.module;
const PriceBoostCoupon = MojitoModules.PriceBoostCoupon;
const { SportCoupon } = MojitoModules;
const CmsSportCoupon = MojitoModules.CmsSportCoupon.module;
const { isCmsCoupon } = MojitoModules.CmsSportCoupon.utils;
const { IntentActionTrigger } = MojitoPresentation.Base.Intent;
const RequestDataHelper = MojitoCore.Services.RequestDataHelper;
const {
    FlexPane,
    DocumentHead,
    GlobalLinks,
    PageHeader,
    PageSplitLayout,
    DefaultHeadMetaFactory,
    EmptyContent,
    PageTitle,
} = MojitoPresentation.Components;
import { selectPreviousRoute } from 'applications/common/stores/router/selectors.js';

class SportCouponPageView extends UIViewImplementation {
    constructor(props) {
        super(props);

        // Creating the MetaFactory instance here to avoid re-creating it on every render()
        const metaFactory = this.config.documentHeadMetaFactory || DefaultHeadMetaFactory;
        this.documentHeadMetaFactory = new metaFactory(this.l10n);
        this.requestDataHelper = new RequestDataHelper(this.instanceId);

        this.onChildLoaded = this.onChildLoaded.bind(this);
        this.onPageHeaderBackClicked = this.onPageHeaderBackClicked.bind(this);
        this.state = {
            dataLoaded: false,
        };
    }

    componentDidUpdate(prevProps) {
        const {
            mojitoTools: { emitPerformanceMark },
        } = this.props;

        emitPerformanceMark('moduleRendered', this.state.dataLoaded);

        if (this.props.couponId !== prevProps.couponId) {
            this.populateData(this.props.couponId);
        }
    }

    componentDidMount() {
        const {
            mojitoTools: { emitPerformanceMark },
        } = this.props;

        emitPerformanceMark('moduleRendered', this.state.dataLoaded);

        this.populateData(this.props.couponId);
    }

    componentWillUnmount() {
        this.requestDataHelper.unsubscribeAll();
    }

    onChildLoaded() {
        if (!this.state.dataLoaded) {
            this.setState({
                dataLoaded: true,
            });
        }
    }

    populateData(couponId) {
        this.requestDataHelper.requestData(
            EventGroupsDataDescriptor.createEventGroupDescriptor(couponId)
        );
    }

    onPageHeaderBackClicked() {
        this.props.previousRoute
            ? window.history.back()
            : IntentActionTrigger.showSport(this.props.sportId);
    }

    renderPageTitle() {
        if (this.config.showPageTitle) {
            const { couponName } = this.props;
            return (
                <PageTitle
                    text={couponName}
                    config={this.config.pageTitle}
                    onClick={this.onPageHeaderBackClicked}
                />
            );
        }
    }

    renderCoupon(sportId, couponId, couponName) {
        const {
            mojitoTools: {
                appContext: { subscriptionResolver },
            },
        } = this.props;

        if (couponId === SportCouponPageView.COUPONS.PRICE_BOOSTS) {
            return (
                <PriceBoostCoupon
                    boostedGroupId={subscriptionResolver.specificSport(sportId)}
                    onDataLoad={this.onChildLoaded}
                />
            );
        } else if (isCmsCoupon(couponId)) {
            return (
                <CmsSportCoupon
                    sportId={sportId}
                    couponId={couponId}
                    couponName={couponName}
                    onDataLoad={this.onChildLoaded}
                />
            );
        }

        return <SportCoupon sportId={sportId} couponId={couponId} />;
    }

    resolveCouponPageHeaderName(couponId, couponName) {
        if (isCmsCoupon(couponId)) {
            return couponName;
        }

        switch (couponId) {
            case SportCouponPageView.COUPONS.TODAY:
                return this.resolveString('$SPORT_COUPON_PAGE_VIEW.TODAY_COUPON.PAGE_HEADER');

            case SportCouponPageView.COUPONS.TOMORROW:
                return this.resolveString('$SPORT_COUPON_PAGE_VIEW.TOMORROW_COUPON.PAGE_HEADER');

            case SportCouponPageView.COUPONS.PRICE_BOOSTS:
                return this.resolveString('$SPORT_COUPON_PAGE_VIEW.PRICE_BOOST_COUPON.PAGE_HEADER');

            default:
                log.error('Unsupported couponId: ', couponId);
        }
    }

    renderPageHeader(couponId, couponName) {
        if (this.config.pageHeaderVisibility.hide) {
            return;
        }

        return (
            <PageHeader
                label={this.resolveCouponPageHeaderName(couponId, couponName)}
                showBackButton={true}
                config={this.config.pageHeader}
                onBackClicked={this.onPageHeaderBackClicked}
            />
        );
    }

    renderDocumentHead(couponId, couponName, sportId) {
        const sportName = sportId && this.appContext().sportName(sportId);
        const coupon = this.resolveCouponPageHeaderName(couponId, couponName);

        if (!sportName || !coupon) {
            return null;
        }

        const headData = {
            title: '$SPORT_COUPON_PAGE_VIEW.PAGE_TITLE',
            keywords: '$SPORT_COUPON_PAGE_VIEW.PAGE_KEYWORDS',
            description: '$SPORT_COUPON_PAGE_VIEW.PAGE_DESCRIPTION',
            canonical: '$SPORT_COUPON_PAGE_VIEW.PAGE_CANONICAL',
            localizedParams: new Map([
                ['sportName', sportName],
                ['couponName', coupon],
            ]),
            params: {
                sportId: sportId,
                couponId: couponId,
            },
        };

        return <DocumentHead data={headData} metaFactory={this.documentHeadMetaFactory} />;
    }

    render() {
        const { couponId, couponName, sportId, showEmptyContent } = this.props;

        return (
            <>
                {this.renderDocumentHead(couponId, couponName, sportId)}
                <PageSplitLayout>
                    {/* Primary content */}
                    {showEmptyContent ? (
                        <EmptyContent config={this.config.emptyContent} />
                    ) : (
                        <div>
                            {this.renderPageTitle()}
                            {this.renderPageHeader(couponId, couponName)}
                            {this.renderCoupon(sportId, couponId, couponName)}
                        </div>
                    )}
                    {/* Secondary content */}
                    <FlexPane config={this.config.secondaryContentContainer}>
                        <SportsMenu selectedSportId={sportId} />
                        <GlobalLinks />
                    </FlexPane>
                </PageSplitLayout>
            </>
        );
    }
}

SportCouponPageView.COUPONS = {
    TODAY: 'today',
    TOMORROW: 'tomorrow',
    PRICE_BOOSTS: 'price-boosts',
};

SportCouponPageView.getStyle = function () {
    return {};
};

const mapStateToProps = state => ({
    previousRoute: selectPreviousRoute(state),
});
export default connect(mapStateToProps)(SportCouponPageView);
